import {
  always,
  concat,
  filter,
  includes,
  innerJoin,
  map,
  path,
  pipe,
  pluck,
  reduce,
  sortBy,
  toLower,
  uniq,
  when,
} from 'ramda'
import { useContext, useState } from 'react'
import LocalStorageContext from '../../../../../context/LocalStorageContext'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const SESSION_CONTENT_CATEGORIES = [
  `Introduction`,
  `Session 1`,
  `Session 2`,
  `Session 3`,
  `Session 4`,
  `Session 5`,
  `Session 6`,
  `Session 7`,
]

const SELECTED_SESSION_OPTION_KEY = `SleepstationResourcesSelectedSessionOption`

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const sortByNameCaseInsensitive = sortBy(toLower)

const getCategoryNames = pluck(`name`)

const categoryToOptionObj = (name) => ({
  label: name,
  value: name,
})

const categoriesToOptionObjs = map(categoryToOptionObj)

const nodesPath = path([`node`, `resourceCategories`, `nodes`])

const getCategoryNamesFromResources = reduce((acc, resource) => {
  const getAllCategoryNames = pipe(nodesPath, getCategoryNames, concat)
  return getAllCategoryNames(resource)(acc)
}, [])

const categoriesToOptions = pipe(
  getCategoryNamesFromResources,
  uniq,
  sortByNameCaseInsensitive,
  categoriesToOptionObjs
)

const hasCategory = (name) => pipe(nodesPath, getCategoryNames, includes(name))

const filterOnCategory = (category) => filter(hasCategory(category))

const getAvailableOptions = (resources) => {
  const options = categoriesToOptions(resources)

  return innerJoin(
    (unfilteredOption, sessionContentCategory) =>
      unfilteredOption.label === sessionContentCategory,
    options,
    SESSION_CONTENT_CATEGORIES
  )
}

const availableOptionsIncludeValue = (value, availableOptions) =>
  pipe(pluck(`value`), includes(value))(availableOptions)

const getDefaultSessionValue = (persistedOption, availableOptions) => {
  if (availableOptions === undefined || availableOptions.length === 0) {
    return null
  }
  if (
    persistedOption &&
    availableOptionsIncludeValue(persistedOption, availableOptions)
  ) {
    return persistedOption
  }
  return availableOptions[0].value
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

const useFilter = (resources, isFiltered) => {
  const availableOptions = getAvailableOptions(resources)

  const localStorageApi = useContext(LocalStorageContext)
  const persistedOption = localStorageApi.getString(SELECTED_SESSION_OPTION_KEY)

  const defaultSessionValue = getDefaultSessionValue(
    persistedOption,
    availableOptions
  )

  const [selectedCategory, setSelectedCategory] = useState(defaultSessionValue)

  if (!isFiltered) {
    return { resolvedResources: resources }
  }

  const setAndSaveSelectedCategory = (value) => {
    setSelectedCategory(value)
    localStorageApi.setValue(SELECTED_SESSION_OPTION_KEY, value)
  }

  const resolvedResources = when(
    always(isFiltered),
    filterOnCategory(selectedCategory),
    resources
  )

  return {
    setSelectedCategory: setAndSaveSelectedCategory,
    resolvedResources,
    availableOptions,
    selectedCategory,
  }
}

export default useFilter
